'use client'

import { useState } from 'react'
import { Error } from '../Error'
import { Width } from '../Width'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Controller } from 'react-hook-form'

import { Calendar, CalendarTimePicker, CalendarTimeZone } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatTypedDate, parseTypedDate } from '@/utilities/parseTypedDate'
import { cn } from '@/utilities/ui'
import {
  buildZonedISO,
  DEFAULT_EVENT_TIMEZONE,
  hasWallClockTime,
  parseZonedISO,
  zonedDateToCalendarDate,
} from '@/utilities/zonedDateTime'

import type { ChangeEventHandler } from 'react'
import type { Control, FieldErrorsImpl } from 'react-hook-form'

const CALENDAR_START_MONTH = new Date(1900, 0)
const CALENDAR_END_MONTH = new Date(new Date().getFullYear() + 10, 11)

type DayPickerProps = {
  name: string
  control: Control
  defaultValue?: string
  errors: Partial<FieldErrorsImpl>
  label?: string
  required?: boolean
  width?: string
  placeholder?: string
  timePicker?: boolean
  /** When set, syncs IANA timezone to this form field (e.g. `start_at_tz`). */
  timezoneName?: string
  defaultTimezone?: string
}

type DayPickerInputProps = {
  name: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  timePicker?: boolean
  timezone: string
  onTimezoneChange: (timeZone: string) => void
}

function DayPickerInput({
  name,
  value,
  onChange,
  placeholder,
  timePicker = false,
  timezone,
  onTimezoneChange,
}: DayPickerInputProps) {
  /** Time chosen before a day is picked; cleared once merged into `value`. */
  const [pendingTime, setPendingTime] = useState('')
  const [open, setOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [visibleMonth, setVisibleMonth] = useState<Date | undefined>()

  const zonedSelected = parseZonedISO(value, timezone)
  const selected = zonedSelected ? zonedDateToCalendarDate(zonedSelected) : undefined
  const formattedDate = selected ? formatTypedDate(selected) : ''
  const inputValue = isEditing ? draft : formattedDate
  const month = visibleMonth ?? selected ?? new Date()

  const timeValue = timePicker
    ? zonedSelected
      ? hasWallClockTime(zonedSelected)
        ? format(zonedSelected, 'HH:mm')
        : pendingTime
      : pendingTime
    : ''

  const emitChange = (day: Date, time?: string) => {
    onChange(buildZonedISO(day, timePicker ? time : undefined, timezone))
  }

  const commitTypedDate = (raw: string) => {
    const parsed = parseTypedDate(raw)
    if (parsed) {
      emitChange(parsed, timePicker ? timeValue || undefined : undefined)
      setVisibleMonth(parsed)
      return
    }
    if (!raw.trim()) {
      onChange('')
      setPendingTime('')
    }
  }

  const handleDaySelect = (date: Date | undefined) => {
    if (!date) {
      onChange('')
      setPendingTime('')
      return
    }

    setVisibleMonth(date)
    setIsEditing(false)

    if (!timePicker || !timeValue) {
      emitChange(date)
      setPendingTime('')
      if (!timePicker) setOpen(false)
      return
    }

    emitChange(date, timeValue)
    setPendingTime('')
  }

  const handleTimeChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const time = event.target.value

    if (!selected) {
      setPendingTime(time)
      return
    }

    emitChange(selected, time)
    if (!time.trim()) {
      setPendingTime('')
    }
  }

  const handleTimezoneChange = (nextTimezone: string) => {
    onTimezoneChange(nextTimezone)
    if (selected) {
      onChange(buildZonedISO(selected, timeValue || undefined, nextTimezone))
    }
  }

  return (
    <div className="relative">
      <Input
        id={name}
        name={name}
        type="text"
        autoComplete="off"
        placeholder={placeholder}
        value={inputValue}
        className="pr-10"
        onChange={(event) => {
          setIsEditing(true)
          setDraft(event.target.value)
        }}
        onFocus={() => {
          setIsEditing(true)
          setDraft(formattedDate)
        }}
        onBlur={() => {
          commitTypedDate(draft)
          setIsEditing(false)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commitTypedDate(draft)
            setIsEditing(false)
            event.currentTarget.blur()
          }
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open calendar"
            className={cn(
              'absolute top-1/2 right-3 -translate-y-1/2 rounded-md p-1 text-muted-foreground',
              'ring-evergreen-500 ring-offset-transparent transition-all outline-none',
              'hover:bg-muted hover:text-foreground',
              'focus-visible:ring focus-visible:ring-offset-2',
            )}
          >
            <CalendarIcon className="size-5" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1" align="start">
          <Calendar
            mode="single"
            selected={selected}
            month={month}
            onMonthChange={setVisibleMonth}
            onSelect={handleDaySelect}
            captionLayout="dropdown"
            startMonth={CALENDAR_START_MONTH}
            endMonth={CALENDAR_END_MONTH}
          />
          {timePicker && (
            <>
              <CalendarTimePicker
                name={`${name}-time`}
                value={timeValue}
                onChange={handleTimeChange}
                className="mt-2 w-full"
              />
              <CalendarTimeZone
                name={`${name}-timezone`}
                value={timezone}
                onValueChange={handleTimezoneChange}
              />
            </>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}

export const DayPicker: React.FC<DayPickerProps> = ({
  name,
  control,
  defaultValue,
  errors,
  label,
  required,
  placeholder = 'Pick a date',
  timePicker = false,
  timezoneName,
  defaultTimezone = DEFAULT_EVENT_TIMEZONE,
}) => {
  const input = (fieldProps: {
    value: string
    onChange: (value: string) => void
    timezone: string
    onTimezoneChange: (timeZone: string) => void
  }) => (
    <DayPickerInput
      name={name}
      value={fieldProps.value}
      onChange={fieldProps.onChange}
      placeholder={placeholder}
      timePicker={timePicker}
      timezone={fieldProps.timezone}
      onTimezoneChange={fieldProps.onTimezoneChange}
    />
  )

  return (
    <Width>
      {label && (
        <Label htmlFor={name} className="mb-1 block">
          {label}
          {required && (
            <span className="required">
              * <span className="sr-only">(required)</span>
            </span>
          )}
        </Label>
      )}
      {timezoneName && timePicker ? (
        <Controller
          control={control}
          name={timezoneName}
          defaultValue={defaultTimezone}
          render={({ field: tzField }) => (
            <Controller
              control={control}
              defaultValue={defaultValue}
              name={name}
              render={({ field }) =>
                input({
                  value: typeof field.value === 'string' ? field.value : '',
                  onChange: field.onChange,
                  timezone: typeof tzField.value === 'string' ? tzField.value : defaultTimezone,
                  onTimezoneChange: tzField.onChange,
                })
              }
              rules={{ required: required ? 'This field is required' : false }}
            />
          )}
        />
      ) : (
        <Controller
          control={control}
          defaultValue={defaultValue}
          name={name}
          render={({ field }) =>
            input({
              value: typeof field.value === 'string' ? field.value : '',
              onChange: field.onChange,
              timezone: defaultTimezone,
              onTimezoneChange: () => {},
            })
          }
          rules={{ required: required ? 'This field is required' : false }}
        />
      )}
      {errors[name] && <Error name={name} />}
    </Width>
  )
}
